import React, { useEffect } from 'react';
import { LoadingScreen, Button, LessonAccordionSummary, ModalOverlay, UnitTabsSummary } from '@punchcard/core';
import AssessmentAccordionSummary from './AssessmentAccordionSummary';
import { useTranslation } from 'react-i18next';
import PlaneStarLogo from '@punchcard/core/assets/plane-stars.png';
import { IconSentSuccess, IconDocumentCheck, IconDownload, IconPreparing, IconRedWarning, IconSend } from '@punchcard/core/icons';
import WorkbookCompletionAccordionSummary from './WorkbookCompletionAccordionSummary';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import courseAPI from '../../api/courseAPI';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { isStudentApp } from '@punchcard/core/utils';
import featureflagAPI from '@punchcard/core/api/featureflagAPI';
import { ACTIVITY_STATUS } from '@punchcard/core/constants/statusConstants';

interface IProps {
	studentName?: string | undefined;
	isWorkSampleFeatureFlagEnabled?: boolean;
	studentId?: string;
	course: StudentCourseDTO | TeacherCourseDTO;
	activeUnit: number;
	handleUnitClick: (index: number) => void;
	handleLessonClick: (id: number) => void;
	activeActivity: number | null;
	handleActivityClick: (index: number | null, currentActivity: currentActivityAssessment) => void;
	safeStatuses: string[];
	openAccordions: number[];
	submitUnitForStudent?: (courseUnitId: number) => void;
	handleWorkbookClick?: () => void;
	handleDownloadClick?: (workSampleFilename: string) => void;
	featureFlagWorkSampleGeneratePdf?: boolean;
	handleWorkSampleToEDI?: () => void;
	setPendingAction?: (action: () => void) => void;
	setShowDiscardModal?: (show: boolean) => void;
	isDirty?: boolean;
	newCalcEnabled?: boolean;
	sendFinalGradeEnabled?: boolean;
	currentActivityItem?: TeacherCourseActivityDTO;
	}

function Summary({
	studentName,
	isWorkSampleFeatureFlagEnabled,
	studentId,
	course,
	activeUnit,
	handleUnitClick,
	handleLessonClick,
	activeActivity,
	handleActivityClick,
	safeStatuses,
	openAccordions,
	submitUnitForStudent,
	handleWorkbookClick,
	handleDownloadClick: handleDownloadClickOriginal,
	featureFlagWorkSampleGeneratePdf,
	handleWorkSampleToEDI,
	setPendingAction,
	setShowDiscardModal,
	isDirty,
	newCalcEnabled,
	sendFinalGradeEnabled,
	currentActivityItem
}: IProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [showConfirmModal, setShowConfirmModal] = React.useState<undefined | 'open' | 'completed'>(undefined);
	const [showWorkSampleModal, setShowWorkSampleModal] = React.useState<undefined | 'open' | 'completed'>(undefined);
	const [showSISModal, setShowSISModal] = React.useState<boolean>(false);
	const [showFinalGradeSent, setFinalGradeSent] = React.useState<boolean>(false);
	const [courseStatusesRefactor, setCourseStatusesRefactorFeature] = React.useState<boolean>(false);

	const courseUnits = course?.courseUnits === undefined ? [] : course.courseUnits;
	const activities = courseUnits[activeUnit].courseActivitySummary;
	const assessments = courseUnits[activeUnit].courseAssessments;

	const unitGradeTotals: string = getUnitGradeTotals(activities, courseUnits[activeUnit].completionGradeValue, courseUnits[activeUnit].gradeReceived);
	const unitGradeTotal: number = getUnitGradeTotal(activities, courseUnits[activeUnit].gradeReceived);
	const [showSpinner, setShowSpinner] = React.useState<boolean>(false);
	const unit = course?.courseUnits[activeUnit];
	const [modalHeader, setModalHeader] = React.useState<string | null>(t('wip.send_to_sis'));
	const [confirmButtonChildren, setConfirmButtonChildren] = React.useState<string | null>(t('wip.send'));
	const [cancelButtonChildren, setCancelButtonChildren] = React.useState<string | null>(t('cancel'));

	const unitCompletedByStudent = React.useMemo(() => {
		const activities = unit?.courseLessons?.flatMap(lesson => lesson.courseActivities as TeacherCourseActivityDTO[]);
		const isActivityCompleted = activities?.every(activity => activity.activityStatus?.statusCode === 'Completed' || activity.activityStatus?.statusCode === 'ReadyForReview');
		const assessments = unit?.courseLessons?.flatMap(lesson => lesson.courseAssessments);
		const isAssessmentCompleted = assessments?.every(assessment => assessment.activityStatus?.statusCode === 'Completed' || assessment.activityStatus?.statusCode === 'ReadyForReview');
		const unitAssessments = unit?.courseAssessments;
		const isUnitAssessmentCompleted = unitAssessments?.every(assessment => assessment.activityStatus?.statusCode === 'Completed' || assessment.activityStatus?.statusCode === 'ReadyForReview');
		const isUnitCompleted = isActivityCompleted && isAssessmentCompleted && isUnitAssessmentCompleted;
		return isUnitCompleted;
	}, [unit?.courseAssessments, unit?.courseLessons]);
	const unitCompletedByTeacher = React.useMemo(() => {
		const isWorkbookCompleted = (unit?.gradeReceived !== undefined || unit.completionGradeValue === undefined);
		const activities = unit?.courseLessons?.flatMap(lesson => lesson.courseActivities);
		const isActivityCompleted = activities?.every(activity => activity.activityStatus?.statusCode === 'Completed');
		const assessments = unit?.courseLessons?.flatMap(lesson => lesson.courseAssessments);
		const isAssessmentCompleted = assessments?.every(assessment => assessment.gradeReceived !== undefined);
		const unitAssessments = unit?.courseAssessments;
		const isUnitAssessmentCompleted = unitAssessments?.every(assessment => assessment.gradeReceived !== undefined);
		return isWorkbookCompleted && isActivityCompleted && isAssessmentCompleted && isUnitAssessmentCompleted;
	}, [unit.completionGradeValue, unit?.courseAssessments, unit?.courseLessons, unit?.gradeReceived]);

	useEffect(() => {
		featureflagAPI.getFeatureFlag({ name: '61035_Refactor_SetCourseStauses' }).then((res : boolean) => {
			setCourseStatusesRefactorFeature(res);
		});
	}, []);

	const unitNeedsGrading = (courseStatusesRefactor && unit.isCompletedByStudent && !unit.isCompletedByTeacher) || (!unitCompletedByTeacher && unitCompletedByStudent);
	const sendGradeToSIS = async () => {
		//there's actually only one...
		const unitDtos: FinalGradeUnitDTO[] = [
			{
				curriculumId: unit.curriculumId!,
				scoreReceived: unitGradeTotal
			}
		];

		const gradeDto: PostFinalGrade = {
			studentId: studentId !== undefined ? parseInt(studentId, 10) : 0,
			courseKey: course.courseKey,
			units: unitDtos
		};

		try {
			const res = await courseAPI.sendFinalGrade(gradeDto);
			if (typeof res === 'undefined' || !res.includes('Success')) {
				if(res.includes('Failed')) {
					toast.error(res);
				}
				else {
					toast.error(t('wip.final_grade_send_fail'));
				}

				throw new Error(res);
			}
			setShowSpinner(false);
			setFinalGradeSent(true);
		} catch (error) {
			setShowSISModal(false);
			setShowSpinner(false);
			setModalHeader(t('wip.send_to_sis'));
			setConfirmButtonChildren(t('wip.send'));
			setCancelButtonChildren(t('cancel'));
		}
	};

	const generatedPdfDate = React.useMemo(() => {
		const { pdfProcessedDate } = unit as TeacherCourseUnitDTO;
		if (pdfProcessedDate === undefined) {
			return '';
		}

		const now = new Date();
		const processedDate = new Date(pdfProcessedDate ?? '');
		const diffInSeconds = Math.floor((now.getTime() - processedDate?.getTime()) / 1000);

		const hours = Math.floor(diffInSeconds / 3600);
		const days = Math.floor(diffInSeconds / 86400);
		const weeks = Math.floor(diffInSeconds / 604800);
		const months = Math.floor(diffInSeconds / 2629800);

		if (hours < 1) { return 'Generated just now'; }
		if (days < 1) { return `Generated ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`; }
		if (weeks < 1) { return `Generated ${days} ${days === 1 ? 'day' : 'days'} ago`; }
		if (months < 1) { return `Generated ${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`; }

		return `Generated ${DateTime.fromISO(pdfProcessedDate).toFormat('MMM dd yyyy hh:nn a')}`;
	}, [unit]);
	const handleReviewClick = () => {
		if (isDirty) {
			setPendingAction?.(() => () => navigate(`/students/${studentId}/course/${course.id}/unit/${course.courseUnits[activeUnit].id}/worksample`));
			setShowDiscardModal?.(true);
			return;
		}
		navigate(`/students/${studentId}/course/${course.id}/unit/${course.courseUnits[activeUnit].id}/worksample`);
	};

	const handleSendWorkSampleToSIS = () => {
		if (isDirty) {
			setPendingAction?.(() => () => setShowWorkSampleModal('open'));
			setShowDiscardModal?.(true);
			return;
		}
		setShowWorkSampleModal('open');
	};

	const handleDownloadClick = (workSampleFilename: string) => {
		if (handleDownloadClickOriginal) {
			handleDownloadClickOriginal(workSampleFilename);
		}
	};

	const handleSubmitToEDI = () => {
		if (isDirty) {
			setPendingAction?.(() => () => setShowSISModal(true)); // Set the pending action to show the SIS modal
			setShowDiscardModal?.(true); // Show the discard modal
			return;
		}
		setShowSISModal(true); // Directly show the SIS modal if the form is not dirty
	};

	const activeUnitNumber = course?.courseUnits[activeUnit]?.unitNumber;

	const activityStatusCodes = React.useMemo(() => (
		unit?.courseLessons?.flatMap(lesson => lesson.courseActivities?.map(a => a.activityStatus?.statusCode) || []) || []
	), [unit?.courseLessons]);
	const assessmentStatusCodes = React.useMemo(() => (
		unit?.courseLessons?.flatMap(lesson => lesson.courseAssessments?.map(a => a.activityStatus?.statusCode) || []) || []
	), [unit?.courseLessons]);
	const unitAssessmentStatusCodes = React.useMemo(() => (
		unit?.courseAssessments?.map(a => a.activityStatus?.statusCode) || []
	), [unit?.courseAssessments]);

	const studentBannerStatus = React.useMemo(() => {
		const allStatuses = [
			...activityStatusCodes,
			...assessmentStatusCodes,
			...unitAssessmentStatusCodes
		];
		const anyOpenOrInProgress = allStatuses.some(status =>
			status === ACTIVITY_STATUS.OPEN ||
			status === ACTIVITY_STATUS.IN_PROGRESS ||
			status === ACTIVITY_STATUS.RE_ATTEMPT
		);
		const unitIsComplete = unit?.courseUnitStatus?.statusCode === ACTIVITY_STATUS.COMPLETED;
		return { anyOpenOrInProgress, unitIsComplete };
	}, [activityStatusCodes, assessmentStatusCodes, unitAssessmentStatusCodes, unit?.courseUnitStatus?.statusCode]);
	const { anyOpenOrInProgress, unitIsComplete } = studentBannerStatus;

	return (
		<div className="d-flex flex-column flex-fill overflow-auto border-top border-gray-500 bg-quaternary-400">
			<UnitTabsSummary course={course} handleUnitClick={handleUnitClick} activeUnit={activeUnit} />
			<div className="bg-white rounded-top-3 flex-fill p-3 overflow-auto d-flex flex-column">
				<h6 className="mb-3">Unit {activeUnitNumber + ': ' + courseUnits[activeUnit]?.unitName}</h6>
				{!isStudentApp && unitNeedsGrading &&
					<div className="bg-light rounded-3 border border-danger-200 p-3 mb-3 d-flex flex-row ">
						<IconRedWarning className="text-danger-600" />
						<div className="d-flex flex-column ms-2">
							<div className="fw-bold d-block">{t('wip.student_has_submitted_unit_work')}</div>
							<div>{t('wip.grade_all_weighted_work')}</div>
						</div>
					</div>
				}
				{course?.courseUnits[activeUnit]?.courseLessons?.map((lesson, index) => (
					<LessonAccordionSummary
						openAccordions={openAccordions}
						lesson={lesson}
						key={lesson.id}
						handleLessonClick={handleLessonClick}
						activeActivity={activeActivity}
						handleActivityClick={handleActivityClick}
						lessonIndex={index + 1}
						safeStatuses={safeStatuses}
					/>
				))}
				{(assessments.length > 0) && (
					<AssessmentAccordionSummary
						assessmentStatus={course?.courseUnits[activeUnit]?.courseAssessmentStatus?.statusCode || 'Open'}
						activities={assessments}
						activeUnit = {activeUnit}
						handleLessonClick={handleLessonClick}
						activeActivity={activeActivity}
						handleActivityClick={handleActivityClick}
						safeStatuses={safeStatuses}
						currentActivityItem={currentActivityItem}
					/>
				)}
				{course?.courseUnits[activeUnit]?.completionGradeValue && <WorkbookCompletionAccordionSummary
					gradeValue={course?.courseUnits[activeUnit]?.completionGradeValue}
					gradeReceived={course?.courseUnits[activeUnit]?.gradeReceived}
					workbookStatus={course?.courseUnits[activeUnit]?.gradeReceived !== undefined ? 'Completed' : 'Open'}
					handleWorkbookClick={handleWorkbookClick}
				/>}
				{unitGradeTotals !== '' && <div className="d-flex justify-content-end mb-3 fw-bold me-1">{unitGradeTotals}</div>}

				{!isStudentApp && sendFinalGradeEnabled &&
					<div className="d-flex justify-content-end mb-3 fw-bold me-1">
						<Button
							className="d-flex btn btn-outline-primary border-2 border-black"
							onClick={handleSubmitToEDI}
							icon={<IconSend className="icon-send" />}
						>
							{t('wip.submit_to_edi')}
						</Button>
					</div>
				}
				{isStudentApp && courseStatusesRefactor && unitIsComplete &&
				<div id="submitted_banner" className="bg-secondary-100 rounded-3 p-3 d-flex flex-column  mt-auto">
					<div className="fw-bold d-block">{t('wip.unit_submitted')}</div>
					<div>{t('wip.your_unit_is_with_your_educator')}</div>
				</div>
				}
				{isStudentApp && courseStatusesRefactor && anyOpenOrInProgress &&
				<div id="submit_banner" className="bg-primary-100 rounded-3 p-3 d-flex flex-row justify-content-between mt-auto">
					<div>
						<div className="fw-bold d-block">{t('wip.finished_the_unit')}</div>
						<div >{t('wip.turn_in_your_work')}</div>
					</div>
					<Button onClick={() => setShowConfirmModal('open')} className="btn-primary">{t('submit')}</Button>
				</div>
				}

				<ModalOverlay
					modalPosition="dialog-centered"
					isOpen={showSISModal}
					modalSize="md"
					onRequestClose={() => {
						setShowSISModal(false);
						setFinalGradeSent(false);
						setModalHeader(t('wip.send_to_sis'));
						setConfirmButtonChildren(t('wip.send'));
						setCancelButtonChildren(t('cancel'));
					}}
					confirmButtonChildren={confirmButtonChildren}
					headerChildren={modalHeader}
					shouldCloseOnOverlayClick={false}
					cancelButtonChildren={cancelButtonChildren}
					hideCloseButton={!showFinalGradeSent}
					confirmButtonAction={() => {
						setShowSpinner(true);
						setModalHeader(null);
						setConfirmButtonChildren(null);
						setCancelButtonChildren(null);
						sendGradeToSIS();
					}}
					cancelButtonAction={() => {
						setShowSISModal(false);
					}}
				>
					{showSpinner &&
						<LoadingScreen className="d-flex flex-column justify-content-center align-items-center flex-fill" />
					}

					{showFinalGradeSent &&
						<div className="d-flex flex-column justify-content-center align-items-center flex-fill">
							<IconSentSuccess className="icon-send-success icon-fill-none" />
							<h4 className="mt-3 mb-3 text-center">{t('wip.final_grade_send_success', { studentName: studentName })}</h4>
						</div>
					}

					{!showSpinner && !showFinalGradeSent && t('wip.send_to_sis_info', { studentName: studentName })}
				</ModalOverlay>


				{!isStudentApp && featureFlagWorkSampleGeneratePdf && (
					<React.Fragment>
						{isWorkSampleFeatureFlagEnabled &&
							<Button className="btn-outline-primary mt-3 mb-3 ms-auto btn-outline-primary-disable-color" onClick={handleSendWorkSampleToSIS}
								icon={<IconSend className="icon-22 icon-fill-none" />}
								disabled={(unit as TeacherCourseUnitDTO).pdfProcessedDate === undefined || (unit as TeacherCourseUnitDTO).sendWorkSampleToEDI !== undefined}
							>
								{t('wip.send_work_sample_to_sis')}
							</Button>
						}
						<div className="bg-secondary-50 border border-secondary-500 rounded-3 p-3 mb-3 mt-auto d-flex flex-row flex-column">
							<h6 >{t('wip.review_and_compile_unit')}</h6>
							<div>{t('wip.review_unit_description')}</div>
							<div className="d-flex flex-row flex-wrap mt-2 align-items-center gap-2">
								<Button className="btn-outline-secondary btn-worksample" onClick={handleReviewClick} icon={<IconDocumentCheck className="icon-22 icon-fill-none" />}>
									{t('wip.review')}
								</Button>
								{(() => {
									const { pdfSubmittedDate, pdfProcessedDate, workSampleFilename } = unit as TeacherCourseUnitDTO;
									const isDisabled = pdfSubmittedDate === undefined || pdfProcessedDate === undefined;
									const isPreparing = pdfSubmittedDate !== undefined && pdfProcessedDate === undefined;
									return (
										<React.Fragment>
											{isPreparing && (
												<Tooltip id={'tooltip-download'} place="top" />
											)}
											<span
												data-tooltip-id="tooltip-download"
												data-tooltip-content={t('wip.work_sample_bein_prepared')}
											>
												<Button
													id="download-work-sample-button"
													className="btn-outline-secondary btn-md btn-worksample"
													disabled={isDisabled}
													icon={isPreparing ? <IconPreparing className="icon-22 icon-fill-none" /> : <IconDownload className="icon-22 icon-fill-none" />}
													onClick={() => handleDownloadClick?.(workSampleFilename)}
												>
													{t(isPreparing ? 'wip.preparing_work_sample' : 'wip.download')}
												</Button>
											</span>
										</React.Fragment>
									);
								})()}

								<p className="mt-auto mb-auto text-info-800 fst-italic">{generatedPdfDate}</p>
							</div>
						</div>
					</React.Fragment>
				)}

			</div>
			<ModalOverlay
				isOpen={showConfirmModal !== undefined}
				modalSize="md"
				onRequestClose={() => setShowConfirmModal(undefined)}
				headerChildren={showConfirmModal === 'open' && t('wip.submit_unit')}
				confirmButtonChildren={showConfirmModal === 'open' && t('submit')}
				cancelButtonChildren={showConfirmModal === 'open' && t('wip.cancel')}
				hideCloseButton={false}
				confirmButtonAction={() => {
					submitUnitForStudent && submitUnitForStudent(course?.courseUnits[activeUnit]?.id);
					setShowConfirmModal('completed');
				}}
				cancelButtonAction={() => {
					setShowConfirmModal(undefined);
				}}
			>
				{showConfirmModal === 'open' ?
					t('wip.are_you_sure_you_want_to_submit_this_unit') :
					<React.Fragment>
						<img src={PlaneStarLogo} alt="Lyla Logo" className="d-block mx-auto px-2 p-5" />
						<h6 className="fw-bold m-3 text-center">{t('wip.all_set_handed_in_your_unit')}</h6>
					</React.Fragment>
				}

			</ModalOverlay>
			<ModalOverlay
				isOpen={showWorkSampleModal !== undefined}
				modalSize="md"
				onRequestClose={() => setShowWorkSampleModal(undefined)}
				headerChildren={showWorkSampleModal === 'open' && t('wip.send_to_the_sis')}
				confirmButtonChildren={showWorkSampleModal === 'open' && t('wip.send_to_sis')}
				cancelButtonChildren={showWorkSampleModal === 'open' && t('cancel')}
				hideCloseButton={false}
				confirmButtonAction={() => {
					handleWorkSampleToEDI && handleWorkSampleToEDI();
					setShowWorkSampleModal('completed');
				}}
				cancelButtonAction={() => {
					setShowWorkSampleModal(undefined);
				}}
			>
				{showWorkSampleModal === 'open' ?
					t('wip.send_student_name_work_sample_to_sis', { studentName: studentName }) :
					<React.Fragment>
						<img src={PlaneStarLogo} alt="Lyla Logo" className="d-block mx-auto px-2 p-5" />
						<h6 className="fw-bold m-3 text-center">{t('wip.sent_to_the_sis')}</h6>
					</React.Fragment>
				}

			</ModalOverlay>
		</div>

	);

	function getUnitGradeTotal(
		activities: (StudentCourseActivityDTO | TeacherCourseActivityDTO)[],
		gradeReceived: number | undefined): number {

		if(newCalcEnabled) {
			return courseUnits[activeUnit].totalCombinedGradeReceived!;
		}
		let totalReceived = gradeReceived ?? 0;
		if (activities) {
			activities.forEach((activity) => {
				totalReceived += activity.gradeReceived ?? 0;
			});
		}
		return totalReceived;
	}

	function getUnitGradeTotals(
		activities: (StudentCourseActivityDTO | TeacherCourseActivityDTO)[],
		workbookCompletionGrade: number | undefined,
		gradeReceived: number | undefined): string {
		if(newCalcEnabled) {
			return `${courseUnits[activeUnit].totalCombinedGradeReceived!}/${courseUnits[activeUnit].totalAchievableGrade!}`;
		}
		let totalGrades = workbookCompletionGrade ?? 0;
		let totalReceived = gradeReceived ?? 0;
		if (activities) {
			activities.forEach((activity) => {
				totalGrades += activity.gradeValue ?? 0;
				totalReceived += activity.gradeReceived ?? 0;
			});
		}
		if (totalGrades === 0) {	// if there are no graded activities, return empty string
			return '';
		}
		return `${totalReceived}/${totalGrades}`;
	}

}

export default Summary;